import {useEffect, useReducer, useRef} from 'react';
import {
  checkIfImageLoadSettled,
  getImageLoadStatus,
  IMAGE_LOAD_STATUS,
  loadImageOnce,
  subscribeImageLoadStatuses,
} from '../../utils/images/imageLoadCache';

/**
 * Предзагрузка списка картинок по порядку и статусы его загрузки. В отличие
 * от useImagesPreloaded — по одной картинке видно, загрузилась ли она или
 * упала, загрузки идут очередью (фоновая подгрузка не забивает канал), а
 * статусы общие на страницу (imageLoadCache): одну картинку из двух списков
 * грузят один раз. Упавшую раньше (на прошлом экране) картинку список
 * пробует снова один раз — новый экран дает новую попытку.
 *
 * @param {string[]} srcs - адреса в порядке загрузки; пустые и повторы пропускаются
 * @param {Object} [options]
 * @param {boolean} [options.isEnabled=true] - false — новых загрузок не начинать
 *   (статусы уже начатых все равно видны)
 * @param {number} [options.concurrency=Infinity] - сколько грузить одновременно
 * @param {'high'|'low'|'auto'} [options.fetchPriority] - приоритет запросов
 * @param {boolean} [options.retain=false] - держать картинки, загрузку которых
 *   начал этот список, пока их адреса в списке: браузер тогда не выгрузит их
 *   из памяти до показа, и показ не ждет повторного чтения
 * @returns {{statuses: Object<string, string|null>, isSettled: boolean}}
 *   статусы по адресу (IMAGE_LOAD_STATUS; null — не начинали) и «у всех
 *   картинок загрузка кончилась» — успехом или ошибкой (пустой список — сразу)
 */
export function useImagePreload(srcs, {
  isEnabled = true, concurrency = Infinity, fetchPriority, retain = false,
} = {}) {
  const uniqueSrcs = [...new Set((srcs || []).filter(Boolean))];
  const key = uniqueSrcs.join('\n');
  const [, rerender] = useReducer((count) => count + 1, 0);
  // Список — прямо при рендере, а не эффектом: загрузка, кончившаяся между
  // рендером и эффектом, иначе прошла бы мимо подписки
  const srcSetRef = useRef(null);
  srcSetRef.current = new Set(uniqueSrcs);
  const retainedRef = useRef(new Map());
  // Повтор упавших — по адресу: упавшую загрузку (с прошлого экрана) хук
  // повторяет один раз — когда впервые видит адрес в списке. Пока повтор не
  // начат, адрес не считается загрузившимся: иначе по старой ошибке список
  // сразу стал бы «дождавшимся», а начатый следом повтор вернул бы его в
  // ожидание (арт над списком мигнул бы). Упавшее уже на этом экране не
  // повторяется — без бесконечных попыток. Отмечаем при рендере, а не в
  // эффекте: от этого зависит isSettled этого же рендера (повтор отметки
  // ничего не меняет — StrictMode не мешает)
  const seenSrcsRef = useRef(new Set());
  const retryPendingSrcsRef = useRef(new Set());
  uniqueSrcs.forEach((src) => {
    if (seenSrcsRef.current.has(src)) {
      return;
    }
    seenSrcsRef.current.add(src);
    if (getImageLoadStatus(src) === IMAGE_LOAD_STATUS.ERROR) {
      retryPendingSrcsRef.current.add(src);
    }
  });

  // Перерисовка — только когда сменился статус картинки из своего списка
  useEffect(() => subscribeImageLoadStatuses((src) => {
    if (srcSetRef.current.has(src)) {
      rerender();
    }
  }), []);

  useEffect(() => {
    // Удержанное — только из нынешнего списка: ушедшие адреса отпускаем,
    // оставшиеся держим и дальше (в кэше ссылки на них уже нет)
    const currentSrcs = new Set(key ? key.split('\n') : []);
    const retained = retainedRef.current;
    retained.forEach((image, src) => {
      if (!retain || !currentSrcs.has(src)) {
        retained.delete(src);
      }
    });

    if (!isEnabled || !key) {
      return undefined;
    }

    let isCancelled = false;
    const queue = [...currentSrcs];
    const retryPendingSrcs = retryPendingSrcsRef.current;
    let activeCount = 0;
    function startNext() {
      while (!isCancelled && activeCount < concurrency && queue.length > 0) {
        const src = queue.shift();
        // Повтор снимается с ожидания, только когда начат: прерванный запуск
        // очереди оставит его следующему
        const isRetry = retryPendingSrcs.delete(src);
        if (checkIfImageLoadSettled(src) && !isRetry) {
          continue;
        }
        // Уже идущую загрузку (ее начал другой список) не повторяем, а ждем
        const {image, promise} = loadImageOnce(src, {fetchPriority, isRetryAfterError: isRetry});
        if (retain && image) {
          retained.set(src, image);
        }
        activeCount += 1;
        promise.then(handleLoadSettled);
      }
    }
    function handleLoadSettled() {
      activeCount -= 1;
      startNext();
    }
    startNext();

    // Отмена — только для очереди: начатые загрузки браузер доведет сам
    return () => {
      isCancelled = true;
    };
  }, [key, isEnabled, concurrency, fetchPriority, retain]);

  // Ушли с экрана — удержанное отпускаем
  useEffect(() => {
    const retained = retainedRef.current;
    return () => retained.clear();
  }, []);

  // Ждущая повтора — для списка еще грузится
  const getStatus = (src) => (retryPendingSrcsRef.current.has(src)
    ? IMAGE_LOAD_STATUS.PENDING
    : getImageLoadStatus(src));
  const statuses = Object.fromEntries(uniqueSrcs.map((src) => [src, getStatus(src)]));
  const isSettled = uniqueSrcs.every((src) => (
    checkIfImageLoadSettled(src) && !retryPendingSrcsRef.current.has(src)
  ));
  return {statuses, isSettled};
}
